/**
 * Pagina principal — fila ao vivo + catalogo de transcricoes concluidas.
 *
 * Tabs: Fila / Concluidas
 * Polling 4s na aba Fila.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import PreviewModal, { type PreviewResultado } from '../components/PreviewModal';
import {
  Captions, Plus, RefreshCw, X, Youtube, Instagram, PlayCircle, Globe,
  CheckCircle2, Clock, Loader2, Search, Trash2, ExternalLink, RotateCw,
} from 'lucide-react';

type Fonte = 'youtube' | 'instagram' | 'panda_video' | 'generic';
type StatusJob = 'pendente' | 'baixando' | 'transcrevendo' | 'concluido' | 'falhou' | 'cancelado';
type Tab = 'fila' | 'concluidas';

interface JobAPI {
  id: string;
  url: string;
  fonte: Fonte;
  status: StatusJob;
  progresso: number;
  erro: string | null;
  iniciadoEm: string | null;
  concluidoEm: string | null;
  criadoEm: string;
  transcricao: { id: string; titulo: string | null; autor: string | null } | null;
}

interface TranscricaoItemAPI {
  id: string;
  urlOrigem: string;
  fonte: Fonte;
  titulo: string | null;
  autor: string | null;
  thumbnailUrl: string | null;
  duracaoSeg: number | null;
  idioma: string | null;
  publicadoEm: string | null;
  custoUsd: string | number | null;
  criadoEm: string;
}

interface EnqueueResponse {
  criados: { id: string; url: string; fonte: string }[];
  ignorados: { url: string; motivo: string }[];
  expansoes: { urlOriginal: string; tipo: 'playlist' | 'channel'; expandidoPara: number; truncado: boolean }[];
}

const FONTE_ICON: Record<Fonte, typeof Youtube> = {
  youtube: Youtube,
  instagram: Instagram,
  panda_video: PlayCircle,
  generic: Globe,
};

const FONTE_LABEL: Record<Fonte, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  panda_video: 'Panda Video',
  generic: 'Genérico',
};

const STATUS_LABEL: Record<StatusJob, string> = {
  pendente: 'Pendente',
  baixando: 'Baixando',
  transcrevendo: 'Transcrevendo',
  concluido: 'Concluído',
  falhou: 'Falhou',
  cancelado: 'Cancelado',
};

export default function Fila() {
  const [tab, setTab] = useState<Tab>('fila');

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Captions className="w-6 h-6 text-purple-600" />
          Transcrições
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Captura áudio de YouTube, Instagram Reels e Panda Video, transcreve via Whisper.
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-6">
        <TabButton active={tab === 'fila'} onClick={() => setTab('fila')}>
          <Clock className="w-4 h-4" /> Fila
        </TabButton>
        <TabButton active={tab === 'concluidas'} onClick={() => setTab('concluidas')}>
          <CheckCircle2 className="w-4 h-4" /> Concluídas
        </TabButton>
      </div>

      {tab === 'fila' && <FilaTab />}
      {tab === 'concluidas' && <ConcluidasTab />}
    </div>
  );
}

// ── Fila ─────────────────────────────────────────────────────────────────────

function FilaTab() {
  const [jobs, setJobs] = useState<JobAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  async function carregar() {
    try {
      const r = await api.get<{ items: JobAPI[]; total: number }>('/jobs', { take: 100 });
      setJobs(r.items);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 4000);
    return () => clearInterval(t);
  }, []);

  const ativos = useMemo(
    () => jobs.filter((j) => j.status === 'baixando' || j.status === 'transcrevendo'),
    [jobs],
  );
  const pendentes = useMemo(() => jobs.filter((j) => j.status === 'pendente'), [jobs]);
  const recentesTerminados = useMemo(
    () => jobs.filter((j) => ['concluido', 'falhou', 'cancelado'].includes(j.status)).slice(0, 20),
    [jobs],
  );

  return (
    <div className="space-y-6">
      {erro && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-400">{erro}</div>
      )}

      <BulkAddBar onCriado={carregar} />

      <section>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <Loader2 className={`w-4 h-4 ${ativos.length ? 'text-purple-500 animate-spin' : 'text-gray-400'}`} />
          Em processamento ({ativos.length}/10)
          {pendentes.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 font-normal ml-2">+ {pendentes.length} aguardando</span>
          )}
        </h2>

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Carregando...</p>
        ) : ativos.length === 0 && pendentes.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-4 text-sm text-gray-500 dark:text-gray-400">
            Nenhum job ativo. Use o campo acima para adicionar URLs.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[...ativos, ...pendentes.slice(0, Math.max(0, 10 - ativos.length))].map((j) => (
              <JobAtivoCard
                key={j.id}
                job={j}
                onCancelar={async () => {
                  await api.post(`/jobs/${j.id}/cancelar`, {});
                  await carregar();
                }}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Recentes</h2>
        {recentesTerminados.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nada por aqui ainda.</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y">
            {recentesTerminados.map((j) => (
              <JobLinha
                key={j.id}
                job={j}
                onDeletar={async () => {
                  if (!confirm('Remover este job?')) return;
                  try {
                    await api.delete(`/jobs/${j.id}`);
                    await carregar();
                  } catch (e) {
                    alert((e as Error).message);
                  }
                }}
                onRetry={async () => {
                  try {
                    await api.post(`/jobs/${j.id}/retry`, {});
                    await carregar();
                  } catch (e) {
                    alert((e as Error).message);
                  }
                }}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function BulkAddBar({ onCriado }: { onCriado: () => void }) {
  const [open, setOpen] = useState(false);
  const [texto, setTexto] = useState('');
  const [carregandoPreview, setCarregandoPreview] = useState(false);
  const [preview, setPreview] = useState<PreviewResultado | null>(null);
  const [resumoCriacao, setResumoCriacao] = useState<EnqueueResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const urls = useMemo(() => {
    return texto
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && /^https?:\/\//i.test(l));
  }, [texto]);

  async function pedirPreview() {
    if (!urls.length) return;
    setCarregandoPreview(true);
    setErro(null);
    setResumoCriacao(null);
    try {
      const r = await api.post<PreviewResultado>('/jobs/preview', { urls });
      setPreview(r);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregandoPreview(false);
    }
  }

  async function confirmarSelecao(urlsSelecionadas: string[]) {
    try {
      const r = await api.post<EnqueueResponse>('/jobs', { urls: urlsSelecionadas });
      setResumoCriacao(r);
      setPreview(null);
      setTexto('');
      onCriado();
    } catch (e) {
      setErro((e as Error).message);
      throw e;
    }
  }

  return (
    <>
      <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
        {!open ? (
          <button
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 text-sm font-medium text-purple-700 dark:text-purple-300 hover:text-purple-900"
          >
            <Plus className="w-4 h-4" /> Adicionar links à fila
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <label className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Cole URLs (1 por linha)
                <span className="block text-xs text-gray-500 dark:text-gray-400 font-normal mt-0.5">
                  Vídeo único OU lista (playlist/canal YouTube, perfil/reels Instagram).
                  Listas vão pra preview pra você escolher quais transcrever (até 20 por lista).
                </span>
              </label>
              <button
                onClick={() => { setOpen(false); setResumoCriacao(null); setErro(null); }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm font-mono"
              placeholder={
                'https://youtube.com/watch?v=...\nhttps://www.instagram.com/USERNAME/reels/\nhttps://b-vz-XXXX.tv.pandavideo.com.br/UUID/360p/video.m3u8'
              }
            />

            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {urls.length === 0 ? 'Nenhuma URL detectada' : `${urls.length} URL${urls.length > 1 ? 's' : ''} para previsualizar`}
              </p>
              <button
                onClick={pedirPreview}
                disabled={!urls.length || carregandoPreview}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded disabled:opacity-50 flex items-center gap-1"
              >
                {carregandoPreview ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando...</> : 'Pré-visualizar'}
              </button>
            </div>

            {erro && <div className="text-sm text-red-700 dark:text-red-400">{erro}</div>}

            {resumoCriacao && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 text-xs">
                <p className="font-medium text-gray-800 dark:text-gray-200">
                  ✓ {resumoCriacao.criados.length} job{resumoCriacao.criados.length === 1 ? '' : 's'} criado{resumoCriacao.criados.length === 1 ? '' : 's'}.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {preview && (
        <PreviewModal
          preview={preview}
          loading={false}
          onCancelar={() => setPreview(null)}
          onConfirmar={confirmarSelecao}
        />
      )}
    </>
  );
}

function JobAtivoCard({ job, onCancelar }: { job: JobAPI; onCancelar: () => void }) {
  const Icon = FONTE_ICON[job.fonte];
  const ativo = job.status === 'baixando' || job.status === 'transcrevendo';
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3">
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-700 dark:text-gray-300 truncate" title={job.url}>{job.url}</p>
          <div className="mt-1 flex items-center gap-2">
            <span className={`text-xs px-1.5 py-0.5 rounded ${ativo ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-600'}`}>
              {STATUS_LABEL[job.status]}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{job.progresso}%</span>
          </div>
          {ativo && (
            <div className="mt-2 h-1.5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
              <div className="h-1.5 bg-purple-500 transition-all" style={{ width: `${job.progresso}%` }} />
            </div>
          )}
        </div>
        {job.status !== 'concluido' && (
          <button onClick={onCancelar} className="text-gray-400 dark:text-gray-500 hover:text-red-600" title="Cancelar">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function JobLinha({ job, onDeletar, onRetry }: { job: JobAPI; onDeletar: () => void; onRetry: () => void }) {
  const Icon = FONTE_ICON[job.fonte];
  const cor =
    job.status === 'concluido' ? 'text-green-600' :
    job.status === 'falhou' ? 'text-red-600' :
    'text-gray-500';
  const podeRetentar = job.status === 'falhou' || job.status === 'cancelado';
  return (
    <div className="px-3 py-2 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900">
      <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
      <div className="min-w-0 flex-1">
        {job.transcricao ? (
          <Link to={`/transcricoes/${job.transcricao.id}`} className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-purple-600 line-clamp-1">
            {job.transcricao.titulo || job.url}
          </Link>
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-1" title={job.url}>{job.url}</p>
        )}
        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <span className={cor}>{STATUS_LABEL[job.status]}</span>
          {job.transcricao?.autor && <><span>·</span><span>{job.transcricao.autor}</span></>}
          <span>·</span><span>{relativo(job.criadoEm)}</span>
          {job.erro && <><span>·</span><span className="text-red-600 truncate" title={job.erro}>{job.erro.slice(0, 80)}</span></>}
        </div>
      </div>
      {podeRetentar && (
        <button
          onClick={onRetry}
          className="text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400"
          title="Tentar novamente (recria o job com a mesma URL)"
        >
          <RotateCw className="w-4 h-4" />
        </button>
      )}
      <button onClick={onDeletar} className="text-gray-400 dark:text-gray-500 hover:text-red-600" title="Remover">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Concluidas ───────────────────────────────────────────────────────────────

function ConcluidasTab() {
  const [busca, setBusca] = useState('');
  const [fonte, setFonte] = useState<Fonte | ''>('');
  const [items, setItems] = useState<TranscricaoItemAPI[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  async function carregar() {
    setLoading(true);
    try {
      const r = await api.get<{ items: TranscricaoItemAPI[]; total: number }>(
        '/transcricoes',
        { busca, fonte, take: 50 },
      );
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(carregar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, fonte]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400 dark:text-gray-500" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar no título, autor ou texto..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
          />
        </div>
        <select
          value={fonte}
          onChange={(e) => setFonte(e.target.value as Fonte | '')}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
        >
          <option value="">Todas as fontes</option>
          <option value="youtube">YouTube</option>
          <option value="instagram">Instagram</option>
          <option value="panda_video">Panda Video</option>
          <option value="generic">Genérico</option>
        </select>
        <button
          onClick={carregar}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Carregando...</p>
      ) : items.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Nenhuma transcrição encontrada.
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">{items.length} de {total}</p>
          <div className="space-y-2">
            {items.map((t) => (
              <TranscricaoCard key={t.id} item={t} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TranscricaoCard({ item }: { item: TranscricaoItemAPI }) {
  const Icon = FONTE_ICON[item.fonte];
  return (
    <Link
      to={`/transcricoes/${item.id}`}
      className="block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-purple-300 transition-colors"
    >
      <div className="flex gap-3">
        {item.thumbnailUrl ? (
          <img
            src={item.thumbnailUrl}
            alt=""
            className="w-32 h-20 object-cover rounded bg-gray-100 dark:bg-gray-700 shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="w-32 h-20 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center shrink-0">
            <Icon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
            {item.titulo || '(sem titulo)'}
          </h3>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="flex items-center gap-1">
              <Icon className="w-3 h-3" />
              {FONTE_LABEL[item.fonte]}
            </span>
            {item.autor && <><span>·</span><span>{item.autor}</span></>}
            {item.duracaoSeg !== null && <><span>·</span><span>{formatarDuracao(item.duracaoSeg)}</span></>}
            {item.idioma && <><span>·</span><span className="uppercase">{item.idioma}</span></>}
            {item.publicadoEm && <><span>·</span><span>{formatarData(item.publicadoEm)}</span></>}
            {item.custoUsd && <><span>·</span><span className="text-gray-400 dark:text-gray-500">{formatarCusto(item.custoUsd)}</span></>}
          </div>
        </div>
        <a
          href={item.urlOrigem}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-gray-300 hover:text-gray-600 dark:text-gray-400 self-start"
          title="Abrir origem"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </Link>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

function relativo(iso: string): string {
  const t = new Date(iso).getTime();
  const now = Date.now();
  const dif = (now - t) / 1000;
  if (dif < 60) return 'agora';
  if (dif < 3600) return `${Math.floor(dif / 60)}min`;
  if (dif < 86400) return `${Math.floor(dif / 3600)}h`;
  return `${Math.floor(dif / 86400)}d`;
}

function formatarDuracao(seg: number): string {
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}min`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

function formatarCusto(v: string | number): string {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return '';
  return `$${n.toFixed(4)}`;
}
